import { useState } from "react";
import { api, type KBEntry, type KBEntrySearchResult } from "@/lib/api-client";
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";

export function KnowledgeBaseDocs() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<KBEntrySearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [entry, setEntry] = useState<KBEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const search = async () => {
    setLoading(true);
    try {
      const res = await api.searchKBEntries({ query, limit: 20 });
      setResults(res.results);
    } finally {
      setLoading(false);
    }
  };

  const loadEntry = async (id: number) => {
    setError("");
    setEntry(null);
    try {
      const res = await api.getKBEntry(id);
      setEntry(res.entry);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load entry");
    }
  };

  const save = async () => {
    if (!entry) return;
    setSaving(true);
    setError("");
    try {
      await api.updateKBEntry(entry.id, {
        title: entry.title,
        tags: entry.tags,
        contentMd: entry.contentMd,
        isActive: entry.isActive,
      });
      await search();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const createEntry = async () => {
    setError("");
    try {
      const res = await api.createKBEntry({ title: "New KB Entry", tags: "", contentMd: "", isActive: true });
      const id = (res as any).entry?.id as number | undefined;
      if (id) {
        setSelectedEntryId(id);
        await loadEntry(id);
      }
      await search();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create entry");
    }
  };

  const deleteEntry = async () => {
    if (!entry) return;
    await api.deleteKBEntry(entry.id);
    setEntry(null);
    setSelectedEntryId(null);
    await search();
  };

  return (
    <CardContent className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2">
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search docs (FTS)" />
            <Button variant="outline" onClick={search} disabled={loading || !query.trim()}>
              Search
            </Button>
          </div>
          <Button variant="outline" onClick={createEntry}>
            New Doc
          </Button>
          <Separator />
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[420px] overflow-auto">
                {results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={async () => {
                      setSelectedEntryId(r.id);
                      await loadEntry(r.id);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-muted/50 ${selectedEntryId === r.id ? "bg-muted" : ""}`}
                  >
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{r.snippet}</div>
                  </button>
                ))}
                {results.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground">
                    {query.trim() ? "No results" : "Search to see docs"}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-3">
          {!entry && !error && <div className="text-sm text-muted-foreground">Select a doc to edit.</div>}
          {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">{error}</div>}
          {entry && (
            <>
              <Input value={entry.title} onChange={(e) => setEntry({ ...entry, title: e.target.value })} placeholder="Title" />
              <Input value={entry.tags} onChange={(e) => setEntry({ ...entry, tags: e.target.value })} placeholder="tags (csv)" />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={entry.isActive} onChange={(e) => setEntry({ ...entry, isActive: e.target.checked })} />
                Active
              </label>
              <Textarea value={entry.contentMd} onChange={(e) => setEntry({ ...entry, contentMd: e.target.value })} className="min-h-[280px] font-mono text-sm" />
              <div className="flex justify-end gap-2">
                <Button onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save Doc"}
                </Button>
                <Button variant="destructive" onClick={deleteEntry}>
                  Delete
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </CardContent>
  );
}

